import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Sparkles, Heart, Star, Users, ArrowRight, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planPrices = { basic: 350, plus: 550, premium: 750 };

export default function MembershipPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedPlan, setSelectedPlan] = useState('plus');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      toast.error(t('membership.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/members/register`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        plan: selectedPlan
      });
      setSuccess(true);
      toast.success(t('membership.successTitle'));
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(t('membership.alreadyMember'));
      } else {
        toast.error(t('membership.errorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  const plans = ['basic', 'plus', 'premium'];

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4EDE6] flex items-center justify-center" data-testid="membership-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto p-8 text-center"
        >
          <div className="w-20 h-20 bg-[#C4A77D] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">
            {t('membership.successTitle')}
          </h1>
          <p className="text-[#595959] mb-6">
            {t('membership.successDesc')}
          </p>
          <Button
            onClick={() => window.location.href = '/upplevelser'}
            className="btn-primary"
            data-testid="membership-success-btn"
          >
            {t('membership.successBtn')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="membership-page">
      {/* Hero */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/membership-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center text-white relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Users className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl mb-6">
              {t('membership.heroTitle')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Membership Description */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-xl text-[#595959] leading-relaxed mb-6">
              {t('membership.whatDesc')}
            </p>
            <p className="text-lg text-[#595959] leading-relaxed mb-6">
              {t('membership.whatDesc2')}
            </p>
            <p className="text-[#C4A77D] font-medium text-xl italic">
              {t('membership.whatTagline')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Membership Plans */}
      <section className="py-16 md:py-20">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-['Avenir_Next'] text-3xl md:text-4xl text-[#1A1A1A] mb-4">
              {t('membership.chooseTitle')}
            </h2>
            <p className="text-[#595959]">{t('membership.chooseDesc')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((planId, i) => {
              const planData = t(`membership.plans.${planId}`);
              const isPopular = planId === 'plus';
              return (
                <motion.div
                  key={planId}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setSelectedPlan(planId)}
                  className={`relative bg-white rounded-2xl p-6 cursor-pointer transition-all flex flex-col ${
                    selectedPlan === planId 
                      ? 'ring-2 ring-[#C4A77D] shadow-lg' 
                      : 'hover:shadow-md'
                  }`}
                  data-testid={`plan-${planId}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#C4A77D] text-white text-xs font-medium px-3 py-1 rounded-full">
                        {t('membership.popular')}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="font-['Avenir_Next'] text-2xl text-[#1A1A1A] mb-2">
                      {planData?.name || planId}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-['Avenir_Next'] text-4xl text-[#C4A77D]">
                        {planPrices[planId]}
                      </span>
                      <span className="text-[#8C8C8C]">{t('membership.perMonth')}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6 flex-1">
                    {(planData?.features || []).map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#595959]">
                        <CheckCircle className="w-4 h-4 text-[#C4A77D] mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className={`w-6 h-6 rounded-full border-2 mx-auto ${
                    selectedPlan === planId 
                      ? 'border-[#C4A77D] bg-[#C4A77D]' 
                      : 'border-[#E8E8E4]'
                  }`}>
                    {selectedPlan === planId && (
                      <Check className="w-full h-full text-white p-0.5" />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-[#595959] mt-8">
            {t('membership.cancelText')}
          </p>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-main">
          <div className="max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#F4EDE6] rounded-2xl p-8"
            >
              <h3 className="font-['Avenir_Next'] text-2xl text-[#1A1A1A] mb-6 text-center">
                {t('membership.formTitle')}
              </h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('membership.name')} *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-12 rounded-xl bg-white"
                    data-testid="member-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('membership.email')} *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-12 rounded-xl bg-white"
                    data-testid="member-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">{t('membership.phone')}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-12 rounded-xl bg-white"
                    data-testid="member-phone"
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#C4A77D]/20">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[#595959]">
                    {t(`membership.plans.${selectedPlan}`)?.name} {t('membership.membership')}
                  </span>
                  <span className="font-medium text-[#1A1A1A]">
                    {planPrices[selectedPlan]} {t('membership.perMonth')}
                  </span>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full btn-primary h-14 text-base"
                  data-testid="become-member-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t('membership.submit')}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-[#8C8C8C] mt-4">
                  {t('membership.termsText')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">
              {t('membership.benefitsTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Sparkles, titleKey: 'membership.pointsMonthly', descKey: 'membership.pointsMonthlyDesc' },
              { icon: Heart, titleKey: 'membership.exclusiveOffers', descKey: 'membership.exclusiveOffersDesc' },
              { icon: Star, titleKey: 'membership.noHiddenFees', descKey: 'membership.noHiddenFeesDesc' },
              { icon: Users, titleKey: 'membership.cancelAnytime', descKey: 'membership.cancelAnytimeDesc' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-[#C4A77D]/20 rounded-full flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-[#C4A77D]" />
                </div>
                <h3 className="font-medium text-[#1A1A1A] mb-1">{t(item.titleKey)}</h3>
                <p className="text-sm text-[#8C8C8C]">{t(item.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
